import { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { COUNTRY_MAP } from '@/lib/countries';
import { getGeoGaps, type GeoGapRow } from '@/api/offers';

const WINDOWS = [7, 30, 90] as const;
const APPS_SHOWN = 3;

// Standard three-form Russian plural: 1 проект, 2 проекта, 5 проектов — the
// "few" form applies to 2-4 except when the teens exception (12-14) kicks in.
function pluralizeRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function StatusBadge({ status }: { status: GeoGapRow['status'] }) {
  const paused = status === 'paused';
  return (
    <span
      title={paused
        ? 'Офер на этот гео есть, но он на паузе'
        : 'Ни одного офера на этот гео'}
      className={`px-1.5 py-0.5 text-[10px] font-medium rounded border whitespace-nowrap ${
        paused
          ? 'bg-warning-soft text-warning border-warning/30'
          : 'bg-danger-soft text-danger border-danger/30'
      }`}
    >
      {paused ? 'НА ПАУЗЕ' : 'НЕТ ОФЕРА'}
    </span>
  );
}

function GapRow({ row, days }: { row: GeoGapRow; days: number }) {
  const country = COUNTRY_MAP[row.code];
  const shown = row.apps.slice(0, APPS_SHOWN);
  const rest = row.apps.length - shown.length;
  return (
    <div className="py-2 border-b border-border last:border-b-0">
      <div className="flex items-center gap-2">
        {country?.flag && <span aria-hidden>{country.flag}</span>}
        <span className="font-medium text-content">{row.code}</span>
        <span className="text-sm text-content-muted truncate">{country?.name || row.code}</span>
        <StatusBadge status={row.status} />
        <span className="ml-auto text-sm tabular-nums text-content whitespace-nowrap">
          {row.users.toLocaleString('ru-RU')}
          <span className="ml-1 text-xs text-content-subtle">юзеров за {days}д</span>
        </span>
      </div>
      {row.pausedPartners.length > 0 && (
        <div className="mt-0.5 text-xs text-content-subtle">
          партнёры на паузе: {row.pausedPartners.join(', ')}
        </div>
      )}
      <div className="mt-0.5 text-xs text-content-subtle">
        {row.projects} {pluralizeRu(row.projects, 'проект', 'проекта', 'проектов')}
        {shown.length > 0 && (
          <span title={row.apps.join(', ')}>
            {' · '}{shown.join(', ')}{rest > 0 ? ` +${rest} ещё` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

export function TrafficGeoGapsModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [days, setDays] = useState<number>(30);
  const [minInput, setMinInput] = useState('50');
  const [copied, setCopied] = useState(false);

  const min = Number.isFinite(Number(minInput)) && minInput.trim() !== ''
    ? Math.max(0, Math.floor(Number(minInput)))
    : 50;

  // Only fetch while the modal is open — the query scans a date range in D1 and
  // must never fire from the Offers page itself. `min` is deliberately NOT part
  // of the query or its key: the endpoint applies `min` as a pure post-filter,
  // so fetching once with min=0 and filtering client-side (below) means typing
  // in the threshold field never triggers another D1 scan. The 10-minute cache
  // covers re-opening the modal or flipping back to an already-seen window —
  // the underlying country sync runs at most once a day per account.
  const { data, isLoading, error } = useQuery({
    queryKey: ['offer-geo-gaps', days],
    queryFn: () => getGeoGaps({ days, min: 0 }),
    enabled: isOpen,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const rawRows = data?.rows ?? [];
  const rows = useMemo(
    () => rawRows.filter((r) => r.users >= min),
    [rawRows, min],
  );
  const hiddenByThreshold = rawRows.length - rows.length;

  const copyList = async () => {
    try {
      await navigator.clipboard.writeText(rows.map((r) => r.code).join(', '));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Трафик без офера" size="lg">
      <div>
        <div className="flex flex-wrap items-end gap-3 pb-3 mb-1 border-b border-border">
          <div>
            <label className="mb-1 block text-xs text-content-subtle">Период</label>
            <div className="inline-flex overflow-hidden rounded-md border border-border text-sm">
              {WINDOWS.map((w, i) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setDays(w)}
                  className={`px-3 py-1.5 ${i > 0 ? 'border-l border-border' : ''} ${
                    days === w
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'bg-surface text-content-muted hover:bg-surface-muted'
                  }`}
                >{w}д</button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-content-subtle">Мин. юзеров</label>
            <input
              type="number"
              min={0}
              value={minInput}
              onChange={(e) => setMinInput(e.target.value)}
              className="w-28 rounded-md border border-border px-3 py-1.5 text-sm"
            />
          </div>
          <span className="pb-1.5 text-xs text-content-subtle">
            Считаем активных юзеров из Firebase по всем проектам. Гео показывается,
            только если активного офера на него нет.
          </span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading && <p className="py-8 text-center text-sm text-content-subtle">Загружаем…</p>}
          {error && (
            <p className="py-8 text-center text-sm text-danger">{(error as Error).message}</p>
          )}
          {!isLoading && !error && rows.length === 0 && (
            <p className="py-8 text-center text-sm text-content-subtle">
              {rawRows.length > 0
                ? `Порог скрывает все ${hiddenByThreshold} гео с трафиком без офера (мин. ${min} юзеров за ${days}д). Попробуй снизить порог.`
                : `За ${days}д нет гео с трафиком без активного офера — либо весь трафик уже закрыт оферами, либо синк Firebase по гео для этого окна ещё не проходил.`}
            </p>
          )}
          {!isLoading && !error && rows.map((r) => <GapRow key={r.code} row={r} days={days} />)}
        </div>

        <div className="flex justify-between items-center pt-4 mt-4 border-t border-border">
          <Button type="button" variant="secondary" onClick={copyList} disabled={rows.length === 0}>
            {copied ? 'Copied!' : 'Copy list'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
